import React, { useState, useEffect, useRef } from 'react';

const HeroSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [videosLoaded, setVideosLoaded] = useState(new Set());
  const [preloadedVideos, setPreloadedVideos] = useState(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [mouseStart, setMouseStart] = useState(null);
  const [mouseEnd, setMouseEnd] = useState(null);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const intervalRef = useRef(null);
  const videoRefs = useRef({});
  const resetTimerRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const scrollIntervalRef = useRef(null);

  // Portfolio videos for the sliding carousel
  const portfolioVideos = [
    "/MML/clip1_home.mp4",
    "/MML/clip2_home.mp4",
    "/MML/clip3_home.mp4",
    "/MML/clip4_home.mp4",
    "/MML/clip5_home.mp4"
  ];

  // Clear existing interval and reset timer
  const clearSlideInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  // Start sliding interval - Enhanced for continuous operation
  const startSlideInterval = () => {
    clearSlideInterval();
    intervalRef.current = setInterval(() => {
      // Only advance if not being interacted with
      if (!isHovered && !isUserInteracting && !isScrolling) {
        setIsTransitioning(true);
        setTimeout(() => {
          setCurrentIndex((prevIndex) => (prevIndex + 1) % portfolioVideos.length);
          setIsTransitioning(false);
        }, 150); // Short transition delay
      }
    }, 3000);
  };

  // Reset timer after user interaction ends
  const resetSlideTimer = () => {
    clearSlideInterval();
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
    }
    
    resetTimerRef.current = setTimeout(() => {
      if (!isHovered && !isUserInteracting && !isScrolling && hasAnimated) {
        console.log('Restarting carousel after user interaction ended');
        startSlideInterval();
      }
    }, 2000); // Wait 2 seconds after interaction ends
  };

  // Auto-slide management - Enhanced for continuous operation
  useEffect(() => {
    if (!hasAnimated) return;

    if (isHovered || isUserInteracting || isScrolling) {
      clearSlideInterval();
    } else {
      // Start sliding after initial animation completes
      const timer = setTimeout(() => {
        startSlideInterval();
      }, 1000); // 1 second after animation completes

      return () => clearTimeout(timer);
    }

    return () => clearSlideInterval();
  }, [isHovered, isUserInteracting, isScrolling, hasAnimated]);

  // Continuous slide monitoring - Ensures carousel never stops
  useEffect(() => {
    if (!hasAnimated) return;

    const monitorInterval = setInterval(() => {
      // Check if carousel should be running but isn't
      if (!isHovered && !isUserInteracting && !isScrolling && !intervalRef.current) {
        console.log('Restarting carousel - interval was lost');
        startSlideInterval();
      }
    }, 2000); // Check every 2 seconds for better responsiveness

    return () => clearInterval(monitorInterval);
  }, [hasAnimated, isHovered, isUserInteracting, isScrolling]);

  // Page visibility and focus handlers to restart carousel
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && hasAnimated && !isHovered && !isUserInteracting && !isScrolling) {
        // Page became visible again, restart carousel if needed
        setTimeout(() => {
          if (!intervalRef.current) {
            startSlideInterval();
          }
        }, 1000);
      }
    };

    const handleFocus = () => {
      if (hasAnimated && !isHovered && !isUserInteracting && !isScrolling) {
        setTimeout(() => {
          if (!intervalRef.current) {
            startSlideInterval();
          }
        }, 1000);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [hasAnimated, isHovered, isUserInteracting, isScrolling]);

  // Continuous slide monitoring - Ensures carousel never stops
  useEffect(() => {
    if (!hasAnimated) return;

    const monitorInterval = setInterval(() => {
      // Check if carousel should be running but isn't
      if (!isHovered && !isUserInteracting && !isScrolling && !intervalRef.current) {
        console.log('Restarting carousel - interval was lost');
        startSlideInterval();
      }
    }, 2000); // Check every 2 seconds for better responsiveness

    return () => clearInterval(monitorInterval);
  }, [hasAnimated, isHovered, isUserInteracting, isScrolling]);

  // Page visibility and focus handlers to restart carousel
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && hasAnimated && !isHovered && !isUserInteracting && !isScrolling) {
        // Page became visible again, restart carousel if needed
        setTimeout(() => {
          if (!intervalRef.current) {
            console.log('Restarting carousel after page became visible');
            startSlideInterval();
          }
        }, 1000);
      }
    };

    const handleFocus = () => {
      if (hasAnimated && !isHovered && !isUserInteracting && !isScrolling) {
        setTimeout(() => {
          if (!intervalRef.current) {
            console.log('Restarting carousel after window focus');
            startSlideInterval();
          }
        }, 1000);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [hasAnimated, isHovered, isUserInteracting, isScrolling]);

  // Trigger animations on component mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setHasAnimated(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearSlideInterval();
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }
    };
  }, []);

  // Block global touch events when touching carousel
  useEffect(() => {
    const blockGlobalTouch = (e) => {
      const carouselElement = e.target.closest('[data-carousel="true"]');
      if (carouselElement) {
        e.stopImmediatePropagation();
        e.stopPropagation();
      }
    };

    // Add global touch event listeners with capture
    document.addEventListener('touchstart', blockGlobalTouch, { capture: true, passive: false });
    document.addEventListener('touchmove', blockGlobalTouch, { capture: true, passive: false });
    document.addEventListener('touchend', blockGlobalTouch, { capture: true, passive: false });

    return () => {
      document.removeEventListener('touchstart', blockGlobalTouch, { capture: true });
      document.removeEventListener('touchmove', blockGlobalTouch, { capture: true });
      document.removeEventListener('touchend', blockGlobalTouch, { capture: true });
    };
  }, []);

  // Preload videos for better performance
  useEffect(() => {
    const preloadVideos = () => {
      portfolioVideos.forEach((videoSrc, index) => {
        if (!preloadedVideos.has(videoSrc)) {
          const video = document.createElement('video');
          video.preload = 'auto'; // Changed from 'metadata' to 'auto'
          video.muted = true;
          video.playsInline = true;
          video.crossOrigin = 'anonymous';
          
          video.addEventListener('loadedmetadata', () => {
            setPreloadedVideos(prev => new Set([...prev, videoSrc]));
          });
          
          video.addEventListener('canplaythrough', () => {
            setVideosLoaded(prev => new Set([...prev, videoSrc]));
          });

          video.addEventListener('loadeddata', () => {
            setVideosLoaded(prev => new Set([...prev, videoSrc]));
          });

          video.addEventListener('error', (e) => {
            console.warn(`Preload failed for: ${videoSrc}`, e);
            // Retry preloading after delay
            setTimeout(() => {
              video.src = videoSrc;
            }, 3000);
          });
          
          video.src = videoSrc;
        }
      });
    };

    // Start preloading immediately after component mounts
    const timer = setTimeout(preloadVideos, 50);
    return () => clearTimeout(timer);
  }, [portfolioVideos]);

  // Handle video load events
  const handleVideoLoad = (videoSrc) => {
    setVideosLoaded(prev => new Set([...prev, videoSrc]));
  };

  const handleVideoError = (videoSrc) => {
    console.warn(`Failed to load video: ${videoSrc}`);
    // Retry loading after a delay
    setTimeout(() => {
      if (videoRefs.current[videoSrc]) {
        videoRefs.current[videoSrc].load();
      }
    }, 2000);
  };

  // Check if all visible videos are loaded
  const areVisibleVideosLoaded = () => {
    const visibleVideos = getVisibleVideos();
    return visibleVideos.every(video => videosLoaded.has(video.src));
  };

  // Get videos to display based on screen size
  const getVisibleVideos = () => {
    const videos = [];
    // Show 1 video on mobile, 2 on tablet, 3 on desktop
    const videoCount = window.innerWidth < 640 ? 1 : window.innerWidth < 1024 ? 2 : 3;
    
    for (let i = 0; i < videoCount; i++) {
      const index = (currentIndex + i) % portfolioVideos.length;
      videos.push({
        src: portfolioVideos[index],
        alt: `MML Concepts Portfolio Video ${index + 1}`,
        key: `${index}-${currentIndex}-${i}` // Unique key for React re-rendering
      });
    }
    return videos;
  };

  // Handle manual slide change
  const handleSlideChange = (index) => {
    if (index !== currentIndex && !isTransitioning) {
      setIsTransitioning(true);
      setIsUserInteracting(true);
      clearSlideInterval();
      
      setTimeout(() => {
        setCurrentIndex(index);
        setIsTransitioning(false);
        setIsUserInteracting(false);
        
        // Reset timer after slide change
        resetSlideTimer();
      }, 150);
    }
  };

  // Touch handlers for mobile swipe with complete isolation
  const handleTouchStart = (e) => {
    // Completely stop all event propagation
    e.nativeEvent.stopImmediatePropagation();
    e.stopPropagation();
    e.stopImmediatePropagation();
    
    setIsUserInteracting(true);
    clearSlideInterval();
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    // Completely stop all event propagation and prevent default
    e.nativeEvent.stopImmediatePropagation();
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    // Completely stop all event propagation
    e.nativeEvent.stopImmediatePropagation();
    e.stopPropagation();
    e.stopImmediatePropagation();
    
    if (!touchStart || !touchEnd) {
      setIsUserInteracting(false);
      resetSlideTimer();
      return;
    }
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      // Swipe left - next slide
      const nextIndex = (currentIndex + 1) % portfolioVideos.length;
      handleSlideChange(nextIndex);
    } else if (isRightSwipe) {
      // Swipe right - previous slide
      const prevIndex = currentIndex === 0 ? portfolioVideos.length - 1 : currentIndex - 1;
      handleSlideChange(prevIndex);
    } else {
      setIsUserInteracting(false);
      resetSlideTimer();
    }
  };

  // Mouse drag handlers for desktop
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setIsUserInteracting(true);
    clearSlideInterval();
    setMouseEnd(null);
    setMouseStart(e.clientX);
    
    // Add global mouse event listeners
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    setMouseEnd(e.clientX);
  };

  const handleMouseUp = (e) => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    // Remove global mouse event listeners
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    
    if (!mouseStart || mouseEnd === null) {
      setIsUserInteracting(false);
      resetSlideTimer();
      return;
    }
    
    const distance = mouseStart - mouseEnd;
    const isLeftDrag = distance > 50;
    const isRightDrag = distance < -50;

    if (isLeftDrag) {
      // Drag left - next slide
      const nextIndex = (currentIndex + 1) % portfolioVideos.length;
      handleSlideChange(nextIndex);
    } else if (isRightDrag) {
      // Drag right - previous slide
      const prevIndex = currentIndex === 0 ? portfolioVideos.length - 1 : currentIndex - 1;
      handleSlideChange(prevIndex);
    } else {
      setIsUserInteracting(false);
      resetSlideTimer();
    }
  };

  // Cleanup mouse events on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // No additional video loading logic

  return (
    <section className="relative bg-secondary shadow-[4px_168px_250px_#0000003f] py-24 md:py-32 lg:py-36 min-h-[85vh] flex items-center">
      <div className="max-w-7xl mx-auto px-4 md:px-6 w-full">
        {/* Header Tagline */}
        <div className={`text-center mb-12 md:mb-16 transition-opacity duration-1500 ${hasAnimated ? 'opacity-100' : 'opacity-0'}`}>
          <p className={`text-primary/80 text-lg md:text-xl lg:text-2xl mb-4 md:mb-6 transition-opacity duration-1800 delay-200 ${hasAnimated ? 'opacity-100' : 'opacity-0'}`}>
            Professional Solutions by <span className="text-gold font-semibold">MML Concepts</span>
          </p>
          <h1 className={`text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-gold leading-tight transition-opacity duration-2000 delay-400 ${hasAnimated ? 'opacity-100' : 'opacity-0'}`}>
            We dont just CREATE!
          </h1>
        </div>

        {/* Carousel Section - Responsive: 1 video on mobile, 2 on tablet, 3 on desktop */}
        <div
          className={`relative mb-12 md:mb-16 w-full touch-pan-y carousel-container ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onMouseDown={!isMobile ? handleMouseDown : undefined}
          onTouchStart={!isMobile ? handleTouchStart : undefined}
          onTouchMove={!isMobile ? handleTouchMove : undefined}
          onTouchEnd={!isMobile ? handleTouchEnd : undefined}
          onTouchCancel={!isMobile ? (e) => {
            e.nativeEvent.stopImmediatePropagation();
            e.stopPropagation();
            e.stopImmediatePropagation();
            setIsUserInteracting(false);
            resetSlideTimer();
          } : undefined}
          style={{ 
            touchAction: isMobile ? 'pan-x' : 'none', // Allow horizontal scroll on mobile, prevent on larger screens
            isolation: 'isolate',
            zIndex: 900, // Reduced to be below navbar
            position: 'relative',
            userSelect: 'none'
          }}
          data-carousel="true"
          data-no-navbar-touch="true"
        >
          {/* Scrollable container for small screens */}
          <div className={`w-full ${hasAnimated ? 'animate-slowPopUp' : 'opacity-0'}`} style={{ animationDuration: '1.4s', animationDelay: '500ms', animationFillMode: 'both' }}>
            {/* Mobile: Horizontal scrollable view, Tablet+: Centered flex view */}
            <div className="sm:hidden">
              {/* Scroll hint for mobile */}
              <div className="text-center mb-2">
                <p className="text-primary/60 text-sm">← Swipe to see more videos →</p>
              </div>
              <div 
                className="flex gap-4 px-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory"
                style={{
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  WebkitOverflowScrolling: 'touch'
                }}
              >
                {portfolioVideos.map((videoSrc, index) => (
                  <div key={`mobile-${index}`} className="relative flex-shrink-0 snap-center">
                    <div className="w-[280px] h-[180px] overflow-hidden shadow-xl rounded-lg transition-all duration-300 ease-in-out hover:shadow-2xl relative">
                      {/* Loading placeholder */}
                      {!videosLoaded.has(videoSrc) && (
                        <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
                          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                      <video
                        ref={(el) => {
                          if (el) videoRefs.current[videoSrc] = el;
                        }}
                        src={videoSrc}
                        className={`w-full h-full object-cover transition-opacity duration-500 ${
                          videosLoaded.has(videoSrc) ? 'opacity-100' : 'opacity-0'
                        }`}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                        onLoadedData={() => handleVideoLoad(videoSrc)}
                        onError={() => handleVideoError(videoSrc)}
                        onCanPlay={() => handleVideoLoad(videoSrc)}
                        onLoadedMetadata={() => {
                          const videoElement = videoRefs.current[videoSrc];
                          if (videoElement) {
                            videoElement.play().catch(() => {
                              console.log('Autoplay prevented for:', videoSrc);
                            });
                          }
                        }}
                        style={{ backgroundColor: '#1c1c1c' }}
                        crossOrigin="anonymous"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Tablet and Desktop: Original carousel view */}
            <div className="hidden sm:block px-2 sm:px-4 md:px-8">
              <div className={`flex justify-center items-center gap-2 sm:gap-4 md:gap-6 lg:gap-8 transition-all duration-300 ease-in-out ${
                isTransitioning ? 'opacity-90 scale-[0.98]' : 
                isDragging || isUserInteracting ? 'opacity-95 scale-[0.99]' : 
                'opacity-100 scale-100'
              }`}>
                {getVisibleVideos().map((video, index) => (
                  <div key={video.key} className={`relative flex-shrink-0 carousel-video-wrapper transition-all duration-300 ease-in-out ${
                    isTransitioning ? 'transform scale-95' : 
                    isDragging ? 'transform scale-[0.98]' : 
                    'transform scale-100'
                  }`}>
                    <div className="w-[280px] h-[180px] xs:w-[300px] xs:h-[190px] sm:w-[320px] sm:h-[200px] md:w-[360px] md:h-[225px] lg:w-[400px] lg:h-[250px] xl:w-[420px] xl:h-[260px] overflow-hidden shadow-xl rounded-lg transition-all duration-300 ease-in-out hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98] relative">
                      {/* Loading placeholder */}
                      {!videosLoaded.has(video.src) && (
                        <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
                          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                      <video
                        ref={(el) => {
                          if (el) videoRefs.current[video.src] = el;
                        }}
                        src={video.src}
                        className={`w-full h-full object-cover transition-opacity duration-500 ${
                          videosLoaded.has(video.src) ? 'opacity-100' : 'opacity-0'
                        }`}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                        onLoadedData={() => handleVideoLoad(video.src)}
                        onError={() => handleVideoError(video.src)}
                        onCanPlay={() => handleVideoLoad(video.src)}
                        onLoadedMetadata={() => {
                          // Force video to start playing once metadata is loaded
                          const videoElement = videoRefs.current[video.src];
                          if (videoElement) {
                            videoElement.play().catch(() => {
                              // Fallback if autoplay fails
                              console.log('Autoplay prevented for:', video.src);
                            });
                          }
                        }}
                        style={{ backgroundColor: '#1c1c1c' }}
                        crossOrigin="anonymous"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Carousel Indicators - Hidden on mobile since it has scrollable view */}
          <div className={`hidden sm:flex justify-center mt-4 sm:mt-6 space-x-1.5 sm:space-x-2 transition-opacity duration-1500 delay-1200 ${hasAnimated ? 'opacity-100' : 'opacity-0'}`}>
            {portfolioVideos.map((_, index) => (
              <button
                key={index}
                onClick={() => handleSlideChange(index)}
                disabled={isTransitioning}
                className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ease-in-out ${
                  index === currentIndex
                    ? 'bg-gold shadow-lg scale-110'
                    : 'bg-primary/30 hover:bg-primary/50 hover:scale-105 active:scale-95'
                } ${isTransitioning ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Description */}
        <div className={`text-center transition-opacity duration-2500 delay-1400 ${hasAnimated ? 'opacity-100' : 'opacity-0'}`}>
          <p className={`text-primary/70 text-lg md:text-xl lg:text-2xl max-w-4xl mx-auto mb-10 md:mb-12 leading-relaxed transition-opacity duration-1800 delay-1600 ${hasAnimated ? 'opacity-100' : 'opacity-0'}`}>
            The ultimate video editing services provider to kickstart your business growth.
          </p>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
